import React, { useEffect } from 'react';
import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getAmenities } from '@/api/properties';
import { useAuth } from '@/contexts/AuthContext';
import type { Amenity } from '@/types/resources/property';

interface AmenitySelectorProps {
  disabled?: boolean;
  onAmenityDataChange?: (amenityData: { id: string; name: string } | null) => void;
}

export const AmenitySelector: React.FC<AmenitySelectorProps> = ({
  disabled = false,
  onAmenityDataChange
}) => {
  const { t } = useTranslation(['assets', 'amenities']);
  const { firestoreUser } = useAuth();
  const form = Form.useFormInstance();

  // Fetch amenities
  const { data: amenitiesData, isLoading } = useQuery({
    queryKey: ['amenities'],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getAmenities({
        firestoreUser,
        pageSize: 100 // Get all amenities for selector
      });
    },
    enabled: !!firestoreUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Initialize amenity data from form values when component mounts or form values change
  useEffect(() => {
    const amenityId = form.getFieldValue('amenityId');
    if (amenityId && amenitiesData?.amenities) {
      const amenity = amenitiesData.amenities.find((a: Amenity) => a.id === amenityId);
      onAmenityDataChange?.(amenity ? { 
        id: amenity.id, 
        name: amenity.name 
      } : null);
    }
  }, [form, amenitiesData, onAmenityDataChange]);

  const handleAmenityChange = (amenityId: string) => {
    // Provide amenity data to parent
    if (amenityId) {
      const amenity = amenitiesData?.amenities?.find((a: Amenity) => a.id === amenityId);
      onAmenityDataChange?.(amenity ? { 
        id: amenity.id, 
        name: amenity.name 
      } : null);
    } else {
      onAmenityDataChange?.(null);
    }
  };

  const amenityOptions = amenitiesData?.amenities?.map((amenity: Amenity) => ({
    label: amenity.name,
    value: amenity.id
  })) || [];

  return (
    <Form.Item
      name="amenityId"
      label={t('assets:fields.amenity')}
    >
      <Select
        placeholder={t('properties:amenity.fields.selectAmenity', 'Select amenity')}
        options={amenityOptions}
        onChange={handleAmenityChange}
        loading={isLoading}
        disabled={disabled}
        allowClear
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
      />
    </Form.Item>
  );
}; 